import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import CRUDs from '../../../../api/CRUDs';
import * as EmpresaAPI from '../../../../api/empresa';
import { getEmpresaScope } from '../../../../helpers/sessionEmpresa';
import { useNavigateParams } from '../../../../hooks/useNavigateParams';
import { useSubmitLock } from '../../../../hooks/useSubmitLock';
import useFlashMessage from '../../../../hooks/useFlashMessage';

const datas = ['dataEleicao', 'inicioMandato', 'fimMandato'];
const horarios = ['inicioInscricao', 'fimInscricao'];
export function useEleicaoModule(permissoes) {
    const location = useLocation();
    const uuid = new URLSearchParams(location.search).get('uuid');
    const editando = Boolean(uuid && uuid !== 'null');
    const { empresaId } = getEmpresaScope();
    const [dados, setDados] = useState({ empresaId, descricao: '', comissao: [] });
    const [dadosSalvos, setDadosSalvos] = useState({});
    const [empresas, setEmpresas] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const geracao = useRef(0);
    const { isSubmitting, runWithLock } = useSubmitLock();
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();
    const podeSalvar = editando ? permissoes.editar : permissoes.incluir;
    useEffect(() => {
        if (!permissoes.autorizado) return;
        const atual = ++geracao.current;
        setCarregando(true); setErro('');
        Promise.all([EmpresaAPI.empresa(empresaId), editando ? CRUDs.eleicao.consulta(uuid) : Promise.resolve(null)])
            .then(([opcoes, response]) => {
                if (atual !== geracao.current) return;
                if (!Array.isArray(opcoes)) throw new Error('Não foi possível carregar as empresas.');
                setEmpresas(opcoes);
                const row = response?.data || { empresaId, descricao: '', comissao: [] };
                const form = { ...row, comissao: row.comissao || [] };
                horarios.forEach((key) => { form[key] = row[key] ? moment(row[key]).format('YYYY-MM-DDTHH:mm:ss') : ''; });
                setDados(form); setDadosSalvos(form);
            })
            .catch((e) => { if (atual === geracao.current) setErro(e.response?.data?.message || e.message); })
            .finally(() => { if (atual === geracao.current) setCarregando(false); });
        return () => { geracao.current += 1; };
    }, [uuid, editando, empresaId, permissoes.autorizado]);

    const voltar = () => {
        const params = new URLSearchParams(location.search);
        params.delete('uuid'); params.delete('btnText');
        navigate('/Eleicao', params);
    };
    const alterar = ({ target: { name, value } }) => {
        if (!podeSalvar || isSubmitting) return;
        if (name === 'empresaId') {
            if (editando) return;
            if (dados.comissao.length && String(value) !== String(dados.empresaId)) {
                setFlashMessage('Remova os membros antes de trocar a empresa.', 'warning'); return;
            }
        }
        setDados((prev) => ({ ...prev, [name]: value }));
    };
    const salvar = (e, itemPendente) => {
        e.preventDefault();
        if (!podeSalvar || carregando || erro) return;
        if (itemPendente) { setFlashMessage('Conclua ou descarte a edição pendente na comissão, nas chapas, no resultado ou nos documentos antes de salvar a eleição.', 'warning'); return; }
        return runWithLock(async () => {
            try {
                if (!dados.empresaId || !dados.descricao?.trim()) throw new Error('Informe empresa e descrição.');
                const payload = {
                    uuid: editando ? uuid : undefined, empresaId: Number(dados.empresaId), descricao: dados.descricao.trim(),
                    localEleicao: dados.localEleicao || null, horaInicioEleicao: dados.horaInicioEleicao || null, horaFimEleicao: dados.horaFimEleicao || null,
                    descricaoMandato: dados.descricaoMandato || null, diretrizes: dados.diretrizes || null, observacao: dados.observacao || null,
                    comissao: dados.comissao.map(({ pessoaId, cargoId, ordem }) => ({ pessoaId: Number(pessoaId), cargoId: Number(cargoId), ordem: Number(ordem) })),
                };
                datas.forEach((key) => { payload[key] = dados[key] || null; });
                horarios.forEach((key) => {
                    const valor = dados[key];
                    if (valor && !moment(valor, ['YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DDTHH:mm'], true).isValid()) throw new Error('Data/hora de inscrição inválida.');
                    payload[key] = valor ? moment(valor).toISOString() : null;
                });
                if (payload.inicioMandato && payload.fimMandato && payload.fimMandato < payload.inicioMandato) throw new Error('Fim do mandato anterior ao início.');
                if (payload.inicioInscricao && payload.fimInscricao && payload.fimInscricao < payload.inicioInscricao) throw new Error('Fim das inscrições anterior ao início.');
                const response = await (editando ? CRUDs.eleicao.editar(payload) : CRUDs.eleicao.cadastrar(payload));
                setFlashMessage(response.data.message, response.data.type || 'success');
                voltar();
            } catch (e) { setFlashMessage(e.response?.data?.message || e.message || 'Erro ao salvar eleição.', 'error'); }
        });
    };
    return { dados, dadosSalvos, setDados, empresas, carregando, erro, editando, podeSalvar, isSubmitting, alterar, salvar, voltar };
}
